import {
  Network, Radio, Signal, HardHat, MapPinned, Wifi,
  Server, Wrench, PackageOpen,
} from "lucide-react";

export const SERVICES = [
  {
    id: "optical",
    index: "01",
    title: "Optical Access Networks",
    tagline: "OLT · GPON · XGS-PON · FTTH",
    icon: Network,
    span: "md:col-span-8",
    image: "https://images.pexels.com/photos/17194840/pexels-photo-17194840.jpeg",
    summary:
      "End-to-end Optical Line Terminal deployment and access network solutions for operators, ISPs and enterprises — installation to lifecycle maintenance.",
    items: [
      "OLT Installation, Integration & Commissioning",
      "OLT Dismantling & Decommissioning",
      "GPON & XGS-PON Network Deployment",
      "FTTH (Fiber to the Home) Rollout",
      "Optical Distribution Network (ODN) Deployment",
      "Fiber Optic Cable Laying & Termination",
      "Fiber Splicing & OTDR Testing",
      "OLT Migration & Network Upgradation",
      "ATP, PAT & Customer Acceptance Testing",
      "Network Health Check & Performance Optimization",
    ],
  },
  {
    id: "microwave",
    index: "02",
    title: "Microwave & Wireless Backhaul",
    tagline: "E-Band · IDU/ODU · LOS",
    icon: Radio,
    span: "md:col-span-4",
    image: "https://images.pexels.com/photos/2352277/pexels-photo-2352277.jpeg",
    summary:
      "Reliable microwave and E-Band radio links engineered for high-capacity transport backhaul.",
    items: [
      "Microwave Radio Installation",
      "E-Band Radio Deployment",
      "Traditional Microwave Links",
      "IDU & ODU Installation",
      "Antenna Mounting & Alignment",
      "Link Commissioning",
      "LOS Survey",
      "Integration & Testing",
    ],
  },
  {
    id: "mobile",
    index: "03",
    title: "Mobile Network Infrastructure",
    tagline: "4G LTE · 5G · BTS / gNodeB",
    icon: Signal,
    span: "md:col-span-4",
    image: "https://images.pexels.com/photos/37730211/pexels-photo-37730211.jpeg",
    summary:
      "Radio access deployment across 4G and 5G — from BTS to gNodeB integration.",
    items: [
      "BTS Installation",
      "eNodeB Installation",
      "gNodeB Installation",
      "4G LTE Deployment",
      "5G Network Deployment",
      "CEN Installation & Commissioning",
      "RF Equipment Installation",
      "Integration Support",
    ],
  },
  {
    id: "civil",
    index: "04",
    title: "Telecom Site Civil Works",
    tagline: "Greenfield · RTT · GBT",
    icon: HardHat,
    span: "md:col-span-8",
    image: "https://images.pexels.com/photos/29411310/pexels-photo-29411310.jpeg",
    summary:
      "Complete civil construction and infrastructure built to operator and OEM standards.",
    items: [
      "Greenfield Site Development",
      "Rooftop (RTT) Site Preparation",
      "Ground-Based Tower (GBT) Civil Works",
      "Foundation Construction for Telecom Towers",
      "Equipment Shelter & BTS Cabin Installation",
      "Boundary Wall & Earthing Pit Construction",
      "Cable Trenching & Duct Installation",
      "RCC Platform & Generator Foundations",
      "Access Road & Compound Development",
      "Tower Strengthening & Site Restoration",
    ],
  },
  {
    id: "deployment",
    index: "05",
    title: "Telecom Site Deployment",
    tagline: "Survey → Acceptance",
    icon: MapPinned,
    span: "md:col-span-6",
    summary:
      "End-to-end site deployment from survey and readiness to integration and final acceptance.",
    items: [
      "Greenfield & Brownfield Site Deployment",
      "Site Survey & Feasibility",
      "Equipment Installation & Cable Dressing",
      "Power Plant & Battery Bank Installation",
      "DG Integration & Rectifier Installation",
      "GPS & Fiber Termination + Testing",
      "Site Integration & Commissioning",
      "ATP, PAT & Customer Acceptance",
    ],
  },
  {
    id: "broadband",
    index: "06",
    title: "Broadband Network Rollouts",
    tagline: "FTTH · Splicing · Connectivity",
    icon: Wifi,
    span: "md:col-span-6",
    summary:
      "Scalable broadband rollouts connecting homes and businesses at speed.",
    items: [
      "FTTH Deployment",
      "Fiber Optic Cable Laying",
      "Broadband Site Installation",
      "Fiber Splicing & Testing",
      "Customer Connectivity",
      "Network Expansion Projects",
    ],
  },
  {
    id: "enterprise",
    index: "07",
    title: "Enterprise Network Solutions",
    tagline: "LAN / WAN · Structured Cabling",
    icon: Server,
    span: "md:col-span-4",
    summary:
      "Robust enterprise connectivity from structured cabling to campus and data center networks.",
    items: [
      "Enterprise LAN/WAN Deployment",
      "Structured Cabling",
      "Network Rack Installation",
      "Data Center Connectivity",
      "Campus Network Implementation",
      "I-WAN Project Execution",
    ],
  },
  {
    id: "om",
    index: "08",
    title: "Operations & Maintenance",
    tagline: "Preventive · Corrective · 24×7",
    icon: Wrench,
    span: "md:col-span-4",
    summary:
      "Keeping networks live with proactive maintenance and rapid breakdown support.",
    items: [
      "Preventive Maintenance",
      "Corrective Maintenance",
      "Fault Rectification",
      "Network Health Checks",
      "Field Operations",
      "Emergency Breakdown Support",
      "Acceptance Testing (ATP/PAT)",
    ],
  },
  {
    id: "material",
    index: "09",
    title: "Telecom Material Supply",
    tagline: "Patch Cords · ODF · Racks",
    icon: PackageOpen,
    span: "md:col-span-4",
    summary:
      "Quality-assured passive components and installation materials, supplied on time.",
    items: [
      "Fiber Patch Cords",
      "Optical Distribution Frames (ODF)",
      "Fiber Accessories",
      "Network Racks",
      "RF Cables & Connectors",
      "Telecom Hardware",
      "Installation Materials",
      "Passive Components & Site Consumables",
    ],
  },
];

export const MANIFESTO = [
  {
    n: "01",
    title: "Execution over promises",
    body: "We operate as a trusted execution partner — turning network designs into live infrastructure with disciplined field delivery.",
  },
  {
    n: "02",
    title: "Quality, safety & compliance",
    body: "Every site is built to operator and OEM standards. Safety and compliance are engineered into the process, not bolted on.",
  },
  {
    n: "03",
    title: "Full-stack telecom capability",
    body: "From civil foundations to fiber, radio and O&M — one accountable partner across the entire deployment lifecycle.",
  },
];

export const STATS = [
  { value: "2025", label: "Established" },
  { value: "9+", label: "Service Verticals" },
  { value: "GPON", label: "& XGS-PON Ready" },
  { value: "24×7", label: "O&M Support" },
];
